import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useTabParam } from '../../shared/ui/useTabParam';

type Status = 'orando' | 'esperando' | 'respondida' | 'cerrada';
type Entry = { id: string; title: string; body: string | null; status: Status; answer: string | null; answeredAt: string | null; sharedAsTestimony: boolean; createdAt: string; updatedAt: string };
const STATUS: Record<Status, string> = { orando: 'Orando', esperando: 'Esperando', respondida: 'Respondida', cerrada: 'Cerrada' };
const day = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });

/** Mi diario de oración: privado. Una oración respondida puede compartirse como testimonio. */
export function JournalPage() {
  const data = useAsync((signal) => api.get<{ items: Entry[] }>('/prayer/me/journal', signal).then((r) => r.items), []);
  const [filter, setFilter] = useTabParam(['abiertas', 'respondidas', 'todas'] as const);
  const [editing, setEditing] = useState<string | null>(null);
  const [sharing, setSharing] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<unknown>, ok: string | null) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      if (ok) setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const items = data.status === 'ready' ? data.data.filter((e) => (filter === 'todas' ? true : filter === 'respondidas' ? e.status === 'respondida' : e.status === 'orando' || e.status === 'esperando')) : [];

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/oracion">← Oración</Link>
      </p>
      <header className="page__header">
        <h1>Mi diario de oración</h1>
        <p className="muted">Solo tú lo ves. Anota por qué oras y cómo responde Dios.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <Card title="Nueva oración">
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const f = new FormData(form);
            void run(() => api.post('/prayer/me/journal', { title: String(f.get('title') ?? '').trim(), body: String(f.get('body') ?? '').trim() || null }), null).then((ok) => ok && form.reset());
          }}
        >
          <Field label="¿Por qué oras?" name="title" required maxLength={150} />
          <div className="field">
            <label htmlFor="j-body">Detalles (opcional)</label>
            <textarea id="j-body" name="body" rows={3} maxLength={4000} />
          </div>
          <Button type="submit" busy={busy}>Guardar</Button>
        </form>
      </Card>
      <div className="tabs" role="tablist" aria-label="Filtro">
        {(['abiertas', 'respondidas', 'todas'] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={filter === k} className={`tabs__tab${filter === k ? ' tabs__tab--active' : ''}`} onClick={() => setFilter(k)}>
            {k === 'abiertas' ? 'Orando' : k === 'respondidas' ? 'Respondidas' : 'Todas'}
          </button>
        ))}
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && items.length === 0 ? <Card><EmptyState title="Nada aquí todavía." /></Card> : null}
      {items.map((e) => (
        <Card key={e.id} title={e.title} action={<span className={`badge ${e.status === 'respondida' ? 'badge--active' : ''}`}>{STATUS[e.status]}</span>}>
          {editing === e.id ? (
            <form
              className="form"
              onSubmit={(ev) => {
                ev.preventDefault();
                const f = new FormData(ev.currentTarget);
                void run(() => api.put(`/prayer/me/journal/${e.id}`, { title: String(f.get('title') ?? '').trim(), body: String(f.get('body') ?? '').trim() || null, status: String(f.get('status')), answer: String(f.get('answer') ?? '').trim() || null }), 'Guardado.').then((ok) => ok && setEditing(null));
              }}
            >
              <Field label="Oración" name="title" defaultValue={e.title} required maxLength={150} />
              <div className="field">
                <label htmlFor={`jb-${e.id}`}>Detalles</label>
                <textarea id={`jb-${e.id}`} name="body" rows={3} maxLength={4000} defaultValue={e.body ?? ''} />
              </div>
              <div className="field">
                <label htmlFor={`js-${e.id}`}>Estado</label>
                <select id={`js-${e.id}`} name="status" defaultValue={e.status}>
                  {Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor={`ja-${e.id}`}>¿Cómo respondió Dios? (opcional)</label>
                <textarea id={`ja-${e.id}`} name="answer" rows={2} maxLength={2000} defaultValue={e.answer ?? ''} />
              </div>
              <div className="actions">
                <Button type="submit" busy={busy}>Guardar</Button>
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
              </div>
            </form>
          ) : (
            <>
              {e.body ? <p className="pre-line">{e.body}</p> : null}
              {e.answer ? <p className="pre-line"><strong>Respuesta:</strong> {e.answer}</p> : null}
              <p className="small muted">Desde el {day(e.createdAt)}{e.answeredAt ? ` · respondida el ${day(e.answeredAt)}` : ''}</p>
              <div className="actions actions--wrap">
                <Button variant="secondary" onClick={() => setEditing(e.id)}>Editar</Button>
                {e.status === 'respondida' && !e.sharedAsTestimony && sharing !== e.id ? (
                  <Button busy={busy} onClick={() => setSharing(e.id)}>Compartir como testimonio</Button>
                ) : null}
                {e.sharedAsTestimony ? <span className="small muted">Compartida como testimonio</span> : null}
                <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('¿Borrar esta oración del diario?')) void run(() => api.delete(`/prayer/me/journal/${e.id}`), 'Borrada.'); }}>Borrar</Button>
              </div>
              {sharing === e.id && !e.sharedAsTestimony ? (
                <div className="form">
                  <p className="small">¿Compartirla con la iglesia como testimonio? Pastoral la revisará antes de publicarla. ¿Cómo quieres que aparezca?</p>
                  <div className="actions actions--wrap">
                    {[false, true].map((anonymous) => (
                      <Button key={String(anonymous)} variant={anonymous ? 'secondary' : 'primary'} busy={busy} onClick={() => void run(() => api.post(`/prayer/me/journal/${e.id}/share`, { anonymous }), 'Enviado como testimonio. Pastoral lo revisará.').then((ok) => ok && setSharing(null))}>
                        {anonymous ? 'Sin mi nombre' : 'Con mi nombre'}
                      </Button>
                    ))}
                    <Button variant="ghost" onClick={() => setSharing(null)}>Cancelar</Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </Card>
      ))}
    </div>
  );
}
